// ui/presenters/codingRuleRows.js
import { i18n } from 'boot/i18n';

/**
 * Turns a funder draft's coding rules into the rows the editor renders, and back.
 *
 * OUT HERE RATHER THAN IN THE DIALOG so the list behaviour can be tested without mounting a form.
 * The dialog owns the controls; this owns what a row says and what editing one does to the draft.
 *
 * A ROW IS KEYED ON WHAT IT REFERENCES — the treatment service, the place — and never on a minted
 * row id. One rule per reference is then a property of the data rather than something the editor has
 * to remember, and a second rule for the same reference is not expressible. It is the arrangement
 * `ChargeLine` uses one aggregate over, for the same reason.
 *
 * A ROW WITH NO RULES SAYS NOTHING. An unconfigured payer is the ordinary state, and eighteen lines
 * of "no rules" is noise that makes the two lines that matter harder to find — so the caption is
 * absent rather than empty, and only a row carrying something describes itself.
 */

/** The label a covered service reads under: its code and its name, the way the picker offers it. */
export function serviceLabel(treatmentService) {
  return [treatmentService?.serviceCode, treatmentService?.serviceName]
    .filter(Boolean)
    .join(' · ');
} // serviceLabel()

/**
 * What a covered-service row says with its rules shut, or null when it carries none.
 *
 * A ROW AT FAULT NAMES THE FAULT INSTEAD OF ITS RULES. The field inside the expansion carries the
 * full explanation, but rows sit shut, so a payer whose Save is refused would otherwise offer nothing
 * to look at: the button would go dead and the reason would be hidden one click down, on a row
 * indistinguishable from the rest. The caption is the row's scannable summary, and when a row cannot
 * be saved the most useful thing it can summarize is why.
 *
 * The payer's code reads before the modifiers, because the modifiers qualify it.
 *
 * @param {{ modifiers?: string[] }} entry
 * @param {boolean} [exceedsLine] The rules on this row would put a fifth modifier on a claim line.
 * @param {boolean} [hasRuleProblem] A conditional rule on this row cannot be saved as it stands.
 * @param {string|null} [payerCode] The code string this payer bills the service under, when it names one.
 * @returns {string|null}
 */
export function serviceCaption(entry, exceedsLine = false, hasRuleProblem = false, payerCode = null) {
  if (exceedsLine) {
    return i18n.t('settings.fundingSources.coding.captionModifiersExceedLine');
  }

  // The line-length fault reads first because it is the one a reader can act on without opening the
  // row: it is about the set as a whole, where an unsaveable rule is about one row inside it.
  if (hasRuleProblem) {
    return i18n.t('settings.fundingSources.modifierRules.captionRuleProblem');
  }

  const modifiers = (entry?.modifiers ?? []).filter(Boolean);
  const ruleCount = (entry?.modifierRules ?? []).length;

  // The rule COUNT rather than what the rules say: a tier list does not fit on a caption line, and
  // the number is what a reader scanning for "does this service branch on who delivered it" needs.
  const parts = [
    payerCode ? i18n.t('settings.fundingSources.coding.captionBillsAs', { code: payerCode }) : null,
    modifiers.length === 0
      ? null
      : i18n.t('settings.fundingSources.coding.captionModifiers', { modifiers: modifiers.join(' ') }),
    ruleCount === 0
      ? null
      : i18n.tc('settings.fundingSources.modifierRules.captionRules', ruleCount, { count: ruleCount }),
  ].filter(Boolean);

  return parts.length === 0 ? null : parts.join(' · ');
} // serviceCaption()

/**
 * What a place row says with its rules shut, or null when the place keeps its own code.
 *
 * IT NAMES THE CODE BEING REPLACED, not just the new one. "Renders as POS 02" is unreadable without
 * opening the place's own record to see what it would otherwise have been, and the whole question a
 * reader brings to this list is whether an override is still worth having. `ownCode` is optional
 * because a place record need not carry a code at all, and inventing one to keep the sentence
 * symmetrical would assert something false.
 *
 * @param {{ placeOfServiceCode?: string }} override
 * @param {string|null} [ownCode] The code the place carries itself.
 * @returns {string|null}
 */
export function placeCaption(override, ownCode = null) {
  const code = override?.placeOfServiceCode?.trim();
  const own = typeof ownCode === 'string' ? ownCode.trim() : '';

  if (!code) {
    return null;
  }

  return own.length > 0 && own !== code
    ? i18n.t('settings.fundingSources.coding.captionRendersAsInstead', { code, ownCode: own })
    : i18n.t('settings.fundingSources.coding.captionRendersAs', { code });
} // placeCaption()

/**
 * The covered-service entry for one treatment service, or null.
 *
 * @param {Array<{ treatmentServiceId: string }>} coveredServices
 * @param {string} treatmentServiceId
 */
export function coveredServiceFor(coveredServices, treatmentServiceId) {
  return (coveredServices ?? [])
    .find((entry) => entry.treatmentServiceId === treatmentServiceId) ?? null;
} // coveredServiceFor()

/**
 * The override for one place, or null.
 *
 * @param {Array<{ placeOfServiceId: string }>} overrides
 * @param {string} placeOfServiceId
 */
export function overrideFor(overrides, placeOfServiceId) {
  return (overrides ?? [])
    .find((entry) => entry.placeOfServiceId === placeOfServiceId) ?? null;
} // overrideFor()

/**
 * The covered services with one entry's modifiers replaced.
 *
 * RETURNS A NEW LIST rather than mutating, so a caller assigning it to the draft cannot also have
 * edited the baseline the dirty check compares against.
 *
 * An empty set is stored as an empty array rather than removing the field: the entry still says the
 * payer covers this service, and only its modifiers went away.
 *
 * @param {Array<Object>} coveredServices
 * @param {string} treatmentServiceId
 * @param {string[]} modifiers
 */
export function withModifiers(coveredServices, treatmentServiceId, modifiers) {
  const clean = (modifiers ?? [])
    .map((modifier) => (typeof modifier === 'string' ? modifier.trim().toUpperCase() : ''))
    .filter((modifier) => modifier.length > 0);

  // Deduplicated because a repeated modifier on a service line is a rejection, not an emphasis, and
  // the chip input has no opinion about what was already there.
  const unique = clean.filter((modifier, index) => clean.indexOf(modifier) === index);

  return (coveredServices ?? []).map((entry) => {
    return entry.treatmentServiceId === treatmentServiceId
      ? { ...entry, modifiers: unique }
      : entry;
  });
} // withModifiers()

/**
 * The covered services with one entry's payer code set, or removed.
 *
 * CLEARING REMOVES THE KEY rather than writing null, because on a covered service an absent field
 * means "use the treatment service's own", and null would be a different statement. See the model.
 *
 * @param {Array<Object>} coveredServices
 * @param {string} treatmentServiceId
 * @param {string|null} serviceCodeId
 */
export function withServiceCode(coveredServices, treatmentServiceId, serviceCodeId) {
  return (coveredServices ?? []).map((entry) => {
    if (entry.treatmentServiceId !== treatmentServiceId) {
      return entry;
    }

    if (typeof serviceCodeId === 'string' && serviceCodeId.length > 0) {
      return { ...entry, serviceCodeId };
    }

    const cleared = { ...entry };
    delete cleared.serviceCodeId;

    return cleared;
  });
} // withServiceCode()

/**
 * The codes a payer may bill one service under, as select options.
 *
 * THE SERVICE'S OWN CODE IS NOT OFFERED, because choosing it overrides nothing and would store a rule
 * that the empty field already expresses.
 *
 * A CODE THE ENTRY ALREADY NAMES IS ADDED BACK when the offered list no longer holds it — retired, or
 * gone from the charge master — so the field shows what is stored rather than reading as empty, the
 * same reason the treatment service editor adds its own reference back.
 *
 * @param {Array<{ id: string, code: string, descriptor?: string }>} offeredCodes - the codes the clinic
 *   bills on, already narrowed to the charge master
 * @param {{ selectedId?: string|null, ownCodeId?: string|null, selectedCode?: Object|null }} context
 * @returns {Array<{ label: string, caption?: string, value: string }>}
 */
export function payerCodeOptions(offeredCodes, { selectedId = null, ownCodeId = null, selectedCode = null } = {}) {
  const asOption = (serviceCode) => ({
    label: serviceCode.code,
    caption: serviceCode.descriptor,
    value: serviceCode.id,
  });
  const options = (offeredCodes ?? [])
    .filter((serviceCode) => serviceCode.id !== ownCodeId)
    .map(asOption);

  if (selectedId !== null && selectedCode !== null && !options.some((option) => option.value === selectedId)) {
    options.unshift(asOption(selectedCode));
  }

  return options;
} // payerCodeOptions()

/**
 * The overrides with one place's code set, keeping the entry even when the code is blank.
 *
 * A BLANK CODE IS A ROW BEING EDITED, NOT A DELETION. An override carrying no code does mean nothing,
 * and it IS dropped — by the mapper, at the point of writing. Dropping it here as well looks like the
 * same rule and is not: this list is the DRAFT, where a row with no code yet is the normal state of
 * one just added, and the state a field sits in for as long as somebody is clearing it to retype. An
 * earlier version removed on blank and cost the add action entirely, because adding a place means
 * adding it with no code and the helper read that as "delete it".
 *
 * Removing a row is `removePlaceOverride`, which is a confirmed action rather than a side effect of
 * emptying a text field.
 *
 * @param {Array<Object>} overrides
 * @param {string} placeOfServiceId
 * @param {string|null} placeOfServiceCode
 */
export function withPlaceOverride(overrides, placeOfServiceId, placeOfServiceCode) {
  const code = typeof placeOfServiceCode === 'string' ? placeOfServiceCode.trim() : '';
  const others = (overrides ?? [])
    .filter((entry) => entry.placeOfServiceId !== placeOfServiceId);

  return others.concat([{ placeOfServiceId, placeOfServiceCode: code }]);
} // withPlaceOverride()
